// @ts-nocheck

import { css } from '@emotion/css';
import * as React from 'react';
import { connect } from 'react-redux';

import {
  clearCreativeImageUrl,
  createAdGroupBatch,
  createCampaignBatch,
  generateCreative,
  saveDiscoAdsCreatorStep,
} from '../../../actions/DiscoAdsActions';

import {
  getCampaignObjectiveForCreativeSetup,
  getCreativeSetup,
  getCreativeSetupImageUrl,
  getCreatorNamespace,
  getCurrentAdGroupIdFromShareable,
  getCurrentCampaignIdFromShareable,
  getDateRangeInfo,
  getDiscoAdsCreatorData,
  getNewAdGroupIdFromCreateAdGroupFlow,
  getNewCampaignIdFromCreateCampaignFlow,
} from '../selectors/pageStateSelectors';

import DiscoAdPreviewer from '../DiscoAdsComponents/CreatorComponents/DiscoAdPreviewer';
import DiscoAdsCreatorFormSection from '../DiscoAdsComponents/CreatorComponents/DiscoAdsCreatorFormSection';
import DiscoAdsCreatorViewComponentWrapper from '../DiscoAdsComponents/CreatorComponents/DiscoAdsCreatorViewComponentWrapper';
import DiscoAdsImageUploader from '../DiscoAdsComponents/CreatorComponents/DiscoAdsImageUploader';

import type { Dispatch, RootState } from 'client/js/redux';
import { isCharLimitExceeded } from '../discoAdsUtils';

import {
  DA_CAMPAIGN_CREATOR_NAMESPACE,
  DA_CREATIVE_CHAR_LIMIT,
  DA_CREATIVE_SETUP,
  DA_CREATOR_BUTTON_COPY,
  DA_CREATOR_FORM_SECTION_COPY,
  INSTALL_CAMPAIGN,
  STEPPER_CARD_COMPLETE_STEP,
} from '../discoAdsConstants';

const HEADLINE = 'headline';
const DESCRIPTION = 'description';
const CONTENT_URL = 'contentUrl';

const formWrapperCustomStylesForCreateFlow = {
  formContainer: {
    display: 'flex',
    borderBottom: '2px solid #f8f8f8',
    minHeight: '630px',
  },
};
const styles = {
  leftSideViewComponent: css({
    width: '600px',
    marginLeft: 50,
    paddingRight: 60,
    paddingTop: 40,
    paddingBottom: 40,
    borderRight: '2px solid #f8f8f8',
  }),
  formSectionsContainerForCreateFlow: css({
    display: 'flex',
    flexDirection: 'column',
    width: '455px',
  }),
};

const getFormSectionsContainerStyle = (createFlow) => {
  if (createFlow) {
    return styles.formSectionsContainerForCreateFlow;
  }
  return css({
    display: 'flex',
    flexDirection: 'column',
  });
};

class DiscoAdsCreateCreative extends React.Component {
  state = {
    headline: '',
    description: '',
    contentUrl: this.props.campaignObjective === INSTALL_CAMPAIGN ? null : '',
  };

  componentDidMount() {
    const { creativeSetupImageUrl, clearCreativeImageUrl } = this.props;
    if (creativeSetupImageUrl) {
      clearCreativeImageUrl();
    }
  }

  componentDidUpdate(prevProps) {
    const { creativeSetup, creatorData, postCreatorData, newCampaignId, newAdGroupId } = this.props;
    if (newCampaignId || newAdGroupId) {
      return; // when either id is defined, then the campaign was already created successfully
    }
    if (prevProps.creativeSetup !== creativeSetup && Object.keys(creativeSetup).length > 1) {
      postCreatorData(creatorData);
    }
  }

  _onInputChange = (e, key) => {
    const { value } = e.target;
    if (key === HEADLINE || key === DESCRIPTION) {
      var valueExceedsCharLimit = isCharLimitExceeded({
        key,
        strLength: value.length,
      });
    }
    this.setState({
      [key]: `${valueExceedsCharLimit ? this.state[key] : value}`,
    });
  };

  _allFieldsArecomplete = () => {
    const { headline, description, contentUrl } = this.state;
    const { creativeSetupImageUrl } = this.props;
    return (
      headline.length &&
      description.length &&
      (contentUrl === null ? true : contentUrl.length) && // conentUrl is null for campaigns with an INSTALL objective
      creativeSetupImageUrl?.length
    );
  };

  _onSaveAndContinueClick = () => {
    const { onCompletionStatusChange, saveDiscoAdsCreatorStep, creativeSetupImageUrl: imageUrl } = this.props;
    saveDiscoAdsCreatorStep({
      stepKey: DA_CREATIVE_SETUP,
      payload: Object.assign({}, this.state, { imageUrl }),
    });
    onCompletionStatusChange && onCompletionStatusChange(STEPPER_CARD_COMPLETE_STEP);
  };

  _handleGenerateCreative = () => {
    const { generateCreative, onCancel: onOpenCloseModal, creativeSetupImageUrl } = this.props;
    const payload = Object.assign({}, this.state, {
      imageUrl: creativeSetupImageUrl,
    });
    generateCreative({ creativeSetup: payload });
    onOpenCloseModal();
  };

  _handleSave = () => {
    if (this.props.creativeModal) {
      return this._handleGenerateCreative();
    }
    this._onSaveAndContinueClick();
  };

  _shouldRenderContentUrlFormSection = () => {
    return this.props.campaignObjective !== INSTALL_CAMPAIGN;
  };

  _getSaveButtonCopy = () => {
    const { creativeModal, creatorNamespace } = this.props;
    const { LAUNCH_CAMPAIGN, LAUNCH_AD_GROUP, SAVE } = DA_CREATOR_BUTTON_COPY;
    if (creativeModal) {
      return SAVE;
    }
    if (creatorNamespace === DA_CAMPAIGN_CREATOR_NAMESPACE) {
      return LAUNCH_CAMPAIGN;
    }
    return LAUNCH_AD_GROUP;
  };

  render() {
    const {
      creative: {
        headline: headlineCopy,
        description: descriptionCopy,
        contentUrl: contentUrlCopy,
        thumbnailImage: thumbnailCopy,
      },
    } = DA_CREATOR_FORM_SECTION_COPY;
    const { onCancel, creativeModal, creativeSetupImageUrl, campaignObjective } = this.props;
    const { headline, description, contentUrl } = this.state;
    const createFlow = !creativeModal; // false when this component is invoked to populate the Add Creative Set modal for ad group management
    return (
      <DiscoAdsCreatorViewComponentWrapper
        saveAndContinueButtonIsDisabled={!this._allFieldsArecomplete()}
        onSaveClick={this._handleSave}
        onCancelClick={onCancel}
        saveButtonCopy={this._getSaveButtonCopy()}
        customStyles={createFlow ? formWrapperCustomStylesForCreateFlow : undefined}
      >
        <div className={createFlow ? styles.leftSideViewComponent : undefined}>
          <div className={getFormSectionsContainerStyle(createFlow)}>
            <DiscoAdsCreatorFormSection
              title={headlineCopy.title}
              subtitle={headlineCopy.subtitle}
              inputPlaceholder={headlineCopy.placeholder}
              inputValue={headline}
              onInputChange={(e) => this._onInputChange(e, HEADLINE)}
              charLimit={DA_CREATIVE_CHAR_LIMIT.headline}
              testid="dac-creative-title"
            />
            <DiscoAdsCreatorFormSection
              title={descriptionCopy.title}
              subtitle={descriptionCopy.subtitle}
              inputPlaceholder={descriptionCopy.placeholder}
              inputValue={description}
              onInputChange={(e) => this._onInputChange(e, DESCRIPTION)}
              charLimit={DA_CREATIVE_CHAR_LIMIT.description}
              testid="dac-creative-description"
            />
            {this._shouldRenderContentUrlFormSection() && (
              <DiscoAdsCreatorFormSection
                title={contentUrlCopy.title}
                subtitle={contentUrlCopy.subtitle}
                inputPlaceholder={contentUrlCopy.placeholder}
                inputValue={contentUrl}
                onInputChange={(e) => this._onInputChange(e, CONTENT_URL)}
                testid="dac-creative-content-url"
              />
            )}
            <DiscoAdsCreatorFormSection
              title={thumbnailCopy.title}
              subtitle={thumbnailCopy.subtitle}
              component={() => <DiscoAdsImageUploader />}
            />
          </div>
        </div>
        {createFlow && (
          <DiscoAdPreviewer
            headline={headline || headlineCopy.placeholder}
            description={description || descriptionCopy.placeholder}
            creativeImageUrl={creativeSetupImageUrl}
            campaignObjective={campaignObjective}
          />
        )}
      </DiscoAdsCreatorViewComponentWrapper>
    );
  }
}

const mapStateToProps = (state: RootState) => ({
  creativeSetup: getCreativeSetup(state),
  creativeSetupImageUrl: getCreativeSetupImageUrl(state),
  creatorNamespace: getCreatorNamespace(state),
  creatorData: getDiscoAdsCreatorData(state),
  currentCampaignId: getCurrentCampaignIdFromShareable(state),
  currentAdGroupId: getCurrentAdGroupIdFromShareable(state),
  campaignObjective: getCampaignObjectiveForCreativeSetup(state),
  newCampaignId: getNewCampaignIdFromCreateCampaignFlow(state),
  newAdGroupId: getNewAdGroupIdFromCreateAdGroupFlow(state),
  dateRangeInfo: getDateRangeInfo(state),
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  saveDiscoAdsCreatorStep: ({ stepKey, payload }) => {
    dispatch(saveDiscoAdsCreatorStep({ stepKey, payload }));
  },
  clearCreativeImageUrl: () => {
    dispatch(clearCreativeImageUrl());
  },
  createCampaignBatch: (creatorData) => {
    dispatch(createCampaignBatch(creatorData));
  },
  createAdGroupBatch: (creatorData) => {
    dispatch(createAdGroupBatch(creatorData));
  },
  generateCreative: (creativeData) => {
    dispatch(generateCreative(creativeData));
  },
});

const mergeProps = (stateProps, dispatchProps, ownProps) => ({
  ...ownProps,
  ...stateProps,
  ...dispatchProps,
  postCreatorData: () => {
    const { creatorData, creatorNamespace, currentCampaignId } = stateProps;
    if (creatorNamespace === DA_CAMPAIGN_CREATOR_NAMESPACE) {
      dispatchProps.createCampaignBatch(creatorData);
      return;
    }
    dispatchProps.createAdGroupBatch({
      campaignId: currentCampaignId,
      ...creatorData,
    });
  },
  generateCreative: (creativeSetup) => {
    const { dateRangeInfo, currentAdGroupId } = stateProps;
    const payload = {
      ...creativeSetup,
      dateRange: dateRangeInfo.dateRange,
      adGroupId: currentAdGroupId,
    };
    dispatchProps.generateCreative(payload);
  },
});

const ConnectedDiscoAdsCreateCreative = connect(mapStateToProps, mapDispatchToProps, mergeProps)(DiscoAdsCreateCreative);

export default ConnectedDiscoAdsCreateCreative;
